import { Spin, Tag } from "antd";
import L from "leaflet";
import { useEffect } from "react";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import { useEquipmentLive } from "@/modules/equipment/hooks/useEquipment";
import type { LiveEquipmentPosition } from "@/modules/equipment/types";

const STATUS_COLORS: Record<string, string> = {
	online: "#22c55e",
	offline: "#6b7280",
	error: "#ef4444",
};

const STATUS_LABELS: Record<string, string> = {
	online: "Online",
	offline: "Offline",
	error: "Xato",
};

function createCarIcon(status: string, size: number) {
	const color = STATUS_COLORS[status] ?? "#6b7280";
	// Top-down car silhouette so it reads correctly on a bird's-eye map, rather
	// than the front-elevation Material glyph.
	return L.divIcon({
		className: "",
		html: `<div style="display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 1px 3px rgba(0,0,0,.35))"><svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24"><rect x="7" y="3" width="10" height="18" rx="3" fill="${color}" stroke="white" stroke-width="1"/><rect x="5.2" y="7.5" width="1.6" height="1.1" rx="0.4" fill="${color}" stroke="white" stroke-width="0.6"/><rect x="17.2" y="7.5" width="1.6" height="1.1" rx="0.4" fill="${color}" stroke="white" stroke-width="0.6"/><rect x="8.5" y="6" width="7" height="4" rx="1.3" fill="white" fill-opacity="0.85"/><rect x="8.5" y="15" width="7" height="2.8" rx="1" fill="white" fill-opacity="0.55"/></svg></div>`,
		iconSize: [size, size],
		iconAnchor: [size / 2, size / 2],
		popupAnchor: [0, -(size / 2 + 2)],
	});
}

function FitBounds({ positions }: { positions: [number, number][] }) {
	const map = useMap();
	useEffect(() => {
		if (positions.length === 0) {
			return;
		}
		if (positions.length === 1) {
			map.setView(positions[0], 12);
			return;
		}
		map.fitBounds(L.latLngBounds(positions), { padding: [30, 30], maxZoom: 14 });
	}, [map, positions]);
	return null;
}

interface SidebarLiveMapProps {
	/** compact=true → mini map in left sidebar (no stats, no popups, no controls) */
	compact?: boolean;
}

export function SidebarLiveMap({ compact = false }: SidebarLiveMapProps) {
	const navigate = useNavigate();
	const { data, isFetching } = useEquipmentLive({});

	const items = data ?? [];
	const withPosition = items.filter(
		(
			e
		): e is LiveEquipmentPosition & { position: NonNullable<LiveEquipmentPosition["position"]> } =>
			e.position !== null
	);
	const positions: [number, number][] = withPosition.map((e) => [
		e.position.latitude,
		e.position.longitude,
	]);

	const onlineCount = items.filter((e) => e.status === "online").length;
	const offlineCount = items.filter((e) => e.status === "offline").length;
	const errorCount = items.filter((e) => e.status === "error").length;

	return (
		<div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
			{/* Stats bar — only in full mode */}
			{!compact && (
				<div
					style={{
						padding: "8px 12px",
						display: "flex",
						flexWrap: "wrap",
						gap: 6,
						alignItems: "center",
						borderBottom: "1px solid #f0f0f0",
						flexShrink: 0,
					}}
				>
					<Tag color="success" style={{ margin: 0, fontSize: 12 }}>
						Online: {onlineCount}
					</Tag>
					<Tag color="default" style={{ margin: 0, fontSize: 12 }}>
						Offline: {offlineCount}
					</Tag>
					<Tag color="error" style={{ margin: 0, fontSize: 12 }}>
						Xato: {errorCount}
					</Tag>
					{isFetching && <Spin size="small" />}
					<span style={{ marginLeft: "auto", fontSize: 11, color: "#9ca3af" }}>
						Jami: {items.length}
					</span>
				</div>
			)}

			{/* Map — fills remaining space */}
			<div style={{ flex: 1, minHeight: 0 }}>
				<MapContainer
					center={[41.56, 60.62]}
					zoom={6}
					style={{ height: "100%", width: "100%" }}
					scrollWheelZoom={!compact}
					zoomControl={!compact}
					attributionControl={!compact}
					dragging={!compact}
					doubleClickZoom={!compact}
					touchZoom={!compact}
				>
					<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
					{positions.length > 0 && <FitBounds positions={positions} />}

					{withPosition.map((eq) => (
						<Marker
							key={eq.equipmentId}
							position={[eq.position.latitude, eq.position.longitude]}
							icon={createCarIcon(eq.status, compact ? 20 : 28)}
						>
							{!compact && (
								<Popup>
									<div style={{ fontSize: 13, minWidth: 160 }}>
										<p style={{ fontWeight: 600, marginBottom: 2 }}>{eq.equipmentName}</p>
										{eq.type && (
											<p style={{ color: "#6b7280", fontSize: 11, marginBottom: 2 }}>
												{eq.type.name}
											</p>
										)}
										{eq.licensePlate && (
											<p style={{ marginBottom: 2 }}>
												Raqam: <strong>{eq.licensePlate}</strong>
											</p>
										)}
										<p style={{ marginBottom: 2 }}>
											Holat:{" "}
											<span
												style={{
													color: STATUS_COLORS[eq.status] ?? "#6b7280",
													fontWeight: 600,
												}}
											>
												{STATUS_LABELS[eq.status] ?? eq.status}
											</span>
										</p>
										{eq.assignedStaff && (
											<p style={{ marginBottom: 4 }}>
												Haydovchi: <strong>{eq.assignedStaff.fullname}</strong>
											</p>
										)}
										<button
											type="button"
											style={{
												color: "#3b82f6",
												fontSize: 12,
												cursor: "pointer",
												background: "none",
												border: "none",
												padding: 0,
												textDecoration: "underline",
											}}
											onClick={() => navigate(`/equipment/${eq.equipmentId}`)}
										>
											GPS tarixini ko'rish →
										</button>
									</div>
								</Popup>
							)}
						</Marker>
					))}
				</MapContainer>
			</div>
		</div>
	);
}
